import apiFetch from './api';

export const normalizeBusinessSearch = (value) => String(value || '')
  .toLowerCase()
  .replace(/[\s&.,'"/\\()+-]/g, '');

const UNIT_PREFIX_RE = /^(?:unit|apartment|apt|suite|ste)\b\.?\s*|^#\s*/i;

export const cleanUnitIdentifier = (value) => {
  let normalized = String(value ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
  while (normalized) {
    const updated = normalized.replace(UNIT_PREFIX_RE, '').trim().replace(/\s+/g, ' ');
    if (updated === normalized) break;
    normalized = updated;
  }
  return normalized;
};

export const formatUnitLabel = (value, { fallback = '' } = {}) => {
  const identifier = cleanUnitIdentifier(value) || cleanUnitIdentifier(fallback);
  return identifier ? `Suite / Unit ${identifier}` : '';
};

// Format a phone number for display. Handles 10-digit and 11-digit
// (US country-code) numbers. Returns '' for blank input so callers can choose
// their own placeholder, and returns the original trimmed value for anything it
// can't parse so a real number is never silently hidden.
export const formatPhoneNumber = (phoneNumber) => {
  if (phoneNumber === null || phoneNumber === undefined) return '';
  const raw = String(phoneNumber).trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length === 10) {
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  return raw;
};

export const formatCodeCitation = (codeOrChapter, sectionValue, { prefix = '\u00A7', fallback = '' } = {}) => {
  const isCodeObject = codeOrChapter && typeof codeOrChapter === 'object';
  const rawChapter = isCodeObject
    ? codeOrChapter.chapter ?? codeOrChapter.chapter_number
    : codeOrChapter;
  const rawSection = isCodeObject
    ? codeOrChapter.section ?? codeOrChapter.section_number
    : sectionValue;

  const chapter = rawChapter === null || rawChapter === undefined ? '' : String(rawChapter).trim();
  let section = rawSection === null || rawSection === undefined ? '' : String(rawSection).trim();

  if (!chapter && !section) return fallback;
  if (!chapter) return `${prefix}${section}`;
  if (!section) return `${prefix}${chapter}`;

  if (section.startsWith(`${chapter}-`)) {
    return `${prefix}${section}`;
  }

  if (section.startsWith(`${chapter}.`)) {
    section = section.slice(chapter.length + 1);
  }

  return `${prefix}${chapter}-${section}`;
};


const SECTION_LABEL_RE = /\([^()]+\)/g;
const ICC_DOTTED_SECTION_RE = /^[A-Z]?\d{3}(?:\.\d+)+$/;
const ROMAN_LABEL_VALUES = {
  i: 1,
  ii: 2,
  iii: 3,
  iv: 4,
  v: 5,
  vi: 6,
  vii: 7,
  viii: 8,
  ix: 9,
  x: 10,
};

const normalizeSectionForHierarchy = (codeOrSection) => {
  const isCodeObject = codeOrSection && typeof codeOrSection === 'object';
  const rawChapter = isCodeObject ? codeOrSection.chapter ?? codeOrSection.chapter_number : null;
  let section = isCodeObject ? codeOrSection.section ?? codeOrSection.section_number : codeOrSection;
  section = section === null || section === undefined ? '' : String(section).trim();
  const chapter = rawChapter === null || rawChapter === undefined ? '' : String(rawChapter).trim();

  if (chapter && section.startsWith(`${chapter}-`)) return section.slice(chapter.length + 1);
  if (chapter && section.startsWith(`${chapter}.`)) return section.slice(chapter.length + 1);
  return section;
};

export const getCodeSectionHierarchy = (codeOrSection) => {
  const section = normalizeSectionForHierarchy(codeOrSection);
  const markerStart = section.indexOf('(');
  if (markerStart >= 0) {
    const base = section.slice(0, markerStart).trim();
    const labels = section.match(SECTION_LABEL_RE) || [];
    return {
      section,
      base,
      labels,
      depth: labels.length,
      parentSection: labels.length ? `${base}${labels.slice(0, -1).join('')}` : null,
    };
  }

  if (ICC_DOTTED_SECTION_RE.test(section)) {
    const parts = section.split('.');
    return {
      section,
      base: parts[0],
      labels: parts.slice(1).map((part) => `.${part}`),
      depth: parts.length - 1,
      parentSection: parts.slice(0, -1).join('.'),
    };
  }

  return {
    section,
    base: section.trim(),
    labels: [],
    depth: 0,
    parentSection: null,
  };
};

export const getCodeBaseSection = (codeOrSection) => getCodeSectionHierarchy(codeOrSection).base;

const compareBaseSection = (left, right) => {
  const leftParts = String(left || '').split('.');
  const rightParts = String(right || '').split('.');
  const length = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < length; index += 1) {
    const a = leftParts[index] ?? '';
    const b = rightParts[index] ?? '';
    const aNumber = /^\d+$/.test(a) ? Number(a) : null;
    const bNumber = /^\d+$/.test(b) ? Number(b) : null;

    if (aNumber !== null && bNumber !== null && aNumber !== bNumber) return aNumber - bNumber;
    if (a !== b) return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }

  return 0;
};

const labelSortValue = (label) => {
  const value = String(label || '').replace(/[().]/g, '');
  if (/^\d+$/.test(value)) return [1, Number(value)];
  const lower = value.toLowerCase();
  if (ROMAN_LABEL_VALUES[lower]) return [4, ROMAN_LABEL_VALUES[lower]];
  if (/^[a-z]$/i.test(value)) return [2, lower.charCodeAt(0)];
  return [9, lower];
};

const compareLabels = (leftLabels, rightLabels) => {
  const length = Math.max(leftLabels.length, rightLabels.length);
  for (let index = 0; index < length; index += 1) {
    if (leftLabels[index] === undefined) return -1;
    if (rightLabels[index] === undefined) return 1;
    const [leftGroup, leftValue] = labelSortValue(leftLabels[index]);
    const [rightGroup, rightValue] = labelSortValue(rightLabels[index]);
    if (leftGroup !== rightGroup) return leftGroup - rightGroup;
    if (leftValue !== rightValue) return leftValue < rightValue ? -1 : 1;
  }
  return 0;
};

export const compareCodeSections = (left, right) => {
  const leftHierarchy = getCodeSectionHierarchy(left);
  const rightHierarchy = getCodeSectionHierarchy(right);
  const baseComparison = compareBaseSection(leftHierarchy.base, rightHierarchy.base);
  if (baseComparison !== 0) return baseComparison;
  return compareLabels(leftHierarchy.labels, rightHierarchy.labels);
};

export const buildCodeHierarchyRows = (codes = []) => [...codes]
  .sort((left, right) => compareCodeSections(left, right))
  .map((code) => ({
    code,
    ...getCodeSectionHierarchy(code),
  }));

const RUN_IN_HEADING_RE = /^(Marginal Markings|Italicized Terms|Deletion indicators|For ease of reference|User note|Chapter History)\b[:.]?\s*(.*)$/i;
const MAJOR_CODE_HEADING_RE = /^(PART\s+\d+\s+[-\u2013\u2014]\s+.+|APPENDIX\s+[A-Z]\b.+|Chapter\s+\d+\b.+)$/i;
const STRUCTURAL_MARKER_RE = /^\(([A-Za-z]|\d+|[ivxlcdm]+)\)\s+(.+)$/i;

const legalMarkerDepth = (label) => {
  const value = String(label || '').replace(/[()]/g, '');
  if (/^\d+$/.test(value)) return 2;
  if (ROMAN_LABEL_VALUES[value.toLowerCase()]) return 4;
  if (/^[A-Z]$/.test(value)) return 3;
  return 1;
};

const normalizeCodeDescriptionText = (value) => String(value || '')
  .replace(/\r\n?/g, '\n')
  .replace(/[ \t]+\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

const prepareCodeDescriptionText = (value) => normalizeCodeDescriptionText(value)
  .replace(/([.:;])\s+(\(([A-Za-z]|\d+|[ivxlcdm]+)\)\s+)/gi, '$1\n$2')
  .replace(/\s+(PART\s+\d+\s+[-\u2013\u2014]\s+)/gi, '\n$1')
  .replace(/\s+(APPENDIX\s+[A-Z]\b)/gi, '\n$1')
  .replace(/\s+(User note:|Chapter History\b)/gi, '\n$1')
  .replace(/\.\s+(Marginal Markings\.|Italicized Terms\.|Deletion indicators\b|For ease of reference\b)/gi, '.\n$1');

export const formatCodeDescriptionBlocks = (value) => {
  const prepared = prepareCodeDescriptionText(value);
  if (!prepared) return [];

  return prepared
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      if (MAJOR_CODE_HEADING_RE.test(line)) {
        return { type: 'heading', text: line };
      }

      const structuralMatch = line.match(STRUCTURAL_MARKER_RE);
      if (structuralMatch) {
        return {
          type: 'labeled',
          label: `(${structuralMatch[1]})`,
          depth: legalMarkerDepth(`(${structuralMatch[1]})`),
          text: structuralMatch[2].trim(),
        };
      }

      const runInMatch = line.match(RUN_IN_HEADING_RE);
      if (runInMatch && runInMatch[2]) {
        return {
          type: 'runIn',
          label: runInMatch[1],
          text: runInMatch[2].trim(),
        };
      }

      return { type: 'paragraph', text: line };
    });
};

// Build a `tel:` href from a phone value. Returns '' for blank input. Strips
// formatting down to bare digits and prefixes +1 for US numbers so links never
// embed punctuation like `tel:(555) 555-5555`.
export const phoneHref = (phoneNumber) => {
  if (phoneNumber === null || phoneNumber === undefined) return '';
  const digits = String(phoneNumber).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
  return `tel:${digits}`;
};

export const formatWebsite = (website) => {
  if (!website) return 'N/A';
  return website.replace(/(^\w+:|^)\/\//, '');
};

export const truncateEmail = (email) => {
  if (!email) return 'N/A';
  return email.split('@')[0];
};

export const roles = {
  0: 'Guest',
  1: 'ONS',
  2: 'OAS',
  3: 'Admin',
  4: 'EDC'
};

export const getRoleName = (role) => {
  return roles[role] || 'Unknown';
};

// Treat users as active unless explicitly marked inactive (active === false)
export const isUserActive = (user) => Boolean(user && user.active !== false);

export const filterActiveOnsUsers = (users) => {
  if (!Array.isArray(users)) return [];
  return users.filter((u) => isUserActive(u));
};

// Fetch the active ONS staff roster. Centralizes the `/users/ons/` fetch +
// active-filter that's otherwise copy-pasted wherever inspectors are selectable.
// `init` is passed through to apiFetch (e.g. { signal }); `authOpts` carries
// { onUnauthorized } for 401 handling. Pass { limit } to request an unpaged
// roster (`?limit=0`). Throws on a non-ok response so callers can decide how to
// surface the failure.
export const fetchActiveOnsUsers = async (init = {}, authOpts = {}, { limit } = {}) => {
  const query = limit === undefined || limit === null ? '' : `?limit=${encodeURIComponent(limit)}`;
  const resp = await apiFetch(`/users/ons/${query}`, init, authOpts);
  if (!resp || !resp.ok) throw new Error('Failed to load inspectors');
  return filterActiveOnsUsers(await resp.json());
};

export const getMostTenuredActiveOnsUser = (users) => {
  const activeOnsUsers = filterActiveOnsUsers(users);
  if (activeOnsUsers.length === 0) return null;

  return [...activeOnsUsers].sort((a, b) => {
    const aTime = a?.created_at ? new Date(a.created_at).getTime() : Number.POSITIVE_INFINITY;
    const bTime = b?.created_at ? new Date(b.created_at).getTime() : Number.POSITIVE_INFINITY;
    if (aTime !== bTime) return aTime - bTime;
    return Number(a?.id || 0) - Number(b?.id || 0);
  })[0];
};

// Collapse a license type / inspection source string down to a comparable key,
// e.g. "Single Family Rental License" and "Single Family License" (the older,
// hardcoded /licenses source string) both become "singlefamily". Lets a license
// type filter match inspections created outside the LicenseApplication flow.
export const normalizeLicenseTypeKey = (value) => String(value || '')
  .toLowerCase()
  .replace(/license|rental|permit/g, '')
  .replace(/[^a-z]/g, '');

// Inspections created via a LicenseApplication carry the authoritative license
// type name(s) through the joined LicenseType (see license_applications on
// InspectionResponse). Returns [] when the inspection has no linked application.
export const getInspectionLicenseTypeNames = (inspection) => {
  const apps = Array.isArray(inspection?.license_applications) ? inspection.license_applications : [];
  return apps.map((a) => a?.license_type_name).filter(Boolean);
};

// True if no filter is set, the inspection's linked application(s) name the
// filtered license type exactly, or (fallback, for inspections with no linked
// application) the inspection's source string normalizes to the same key as
// the filtered license type name.
export const matchesLicenseTypeFilter = (inspection, filterName, rawTypeFallback) => {
  if (!filterName) return true;
  if (getInspectionLicenseTypeNames(inspection).includes(filterName)) return true;
  const targetKey = normalizeLicenseTypeKey(filterName);
  if (!targetKey) return false;
  const source = rawTypeFallback ?? inspection?.source;
  return normalizeLicenseTypeKey(source) === targetKey;
};

// Applicant-friendly identifier for a license application: the business name,
// property name, or address the applicant recognizes. Null when none is set —
// callers fall back to the license type name.
export const getApplicationDisplayName = (application) => {
  const candidates = [application?.business_name, application?.property_name, application?.combadd];
  for (const value of candidates) {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (trimmed) return trimmed;
  }
  return null;
};

// Shared helpers to consistently render timestamps in the jurisdiction's local timezone
export const EASTERN_TIME_ZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

const normalizeDateInput = (value) => {
  if (value === null || value === undefined) return null;
  // If value is a string looking like "YYYY-MM-DDTHH:MM:SS" (naive), append EST offset
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) {
    // Treat naive ISO timestamps as UTC; server stores comment times in UTC
    value = value + 'Z';
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const parseEasternDateInput = (value) => normalizeDateInput(value);

const normalizeLocaleArgs = (locales, options) => {
  let resolvedLocales = locales;
  let resolvedOptions = options;

  if (
    resolvedOptions === undefined &&
    resolvedLocales &&
    typeof resolvedLocales === 'object' &&
    !Array.isArray(resolvedLocales)
  ) {
    resolvedOptions = resolvedLocales;
    resolvedLocales = undefined;
  }

  return {
    locales: resolvedLocales,
    options: resolvedOptions || {}
  };
};

export const toEasternLocaleString = (value, locales, options) => {
  const date = normalizeDateInput(value);
  if (!date) return '';

  const { locales: resolvedLocales, options: resolvedOptions } = normalizeLocaleArgs(locales, options);

  return date.toLocaleString(resolvedLocales, {
    ...resolvedOptions,
    timeZone: EASTERN_TIME_ZONE
  });
};

export const toEasternLocaleTimeString = (value, locales, options) => {
  const date = normalizeDateInput(value);
  if (!date) return '';

  const { locales: resolvedLocales, options: resolvedOptions } = normalizeLocaleArgs(locales, options);

  return date.toLocaleTimeString(resolvedLocales, {
    ...resolvedOptions,
    timeZone: EASTERN_TIME_ZONE
  });
};

export const toEasternLocaleDateString = (value, locales, options) => {
  // Calendar dates have no instant or timezone. Format at UTC to preserve the
  // literal day; timestamps below still use the jurisdiction's timezone.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const { locales: resolvedLocales, options: resolvedOptions } = normalizeLocaleArgs(locales, options);
    const date = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(resolvedLocales, { ...resolvedOptions, timeZone: 'UTC' });
  }
  const date = normalizeDateInput(value);
  if (!date) return '';

  const { locales: resolvedLocales, options: resolvedOptions } = normalizeLocaleArgs(locales, options);

  return date.toLocaleDateString(resolvedLocales, {
    ...resolvedOptions,
    timeZone: EASTERN_TIME_ZONE
  });
};

// Display only: keep ISO keys in form values, comparisons and API payloads.
export const formatDisplayDate = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) return '';
    return `${value.slice(5, 7)}-${value.slice(8, 10)}-${value.slice(0, 4)}`;
  }
  const date = normalizeDateInput(value);
  if (!date) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type).value;
  return `${part('month')}-${part('day')}-${part('year')}`;
};

export const toCalendarDateInput = (value) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return value ? toEasternDateKey(value) || '' : '';
};

const getEasternDateTimeParts = (value) => {
  const date = normalizeDateInput(value);
  if (!date) return null;

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(date);

  const mapped = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  return {
    year: mapped.year,
    month: mapped.month,
    day: mapped.day,
    hour: mapped.hour === '24' ? '00' : mapped.hour,
    minute: mapped.minute,
  };
};

export const toEasternDateKey = (value) => {
  const parts = getEasternDateTimeParts(value);
  if (!parts) return '';
  return `${parts.year}-${parts.month}-${parts.day}`;
};

// Today on the town's calendar (YYYY-MM-DD). Use instead of
// new Date().toISOString().slice(0, 10), which is tomorrow after 8 PM Eastern.
export const easternToday = () => toEasternDateKey(new Date());

/** The Eastern calendar date `days` after `from` (default today), as YYYY-MM-DD. */
export const easternDateKeyAfterDays = (days, from = easternToday()) => {
  const date = new Date(`${from}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

export const toEasternDateTimeLocalInputValue = (value) => {
  const parts = getEasternDateTimeParts(value);
  if (!parts) return '';
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

export const toEasternTimeInputValue = (value, fallback = '09:00') => {
  const parts = getEasternDateTimeParts(value);
  if (!parts) return fallback;
  return `${parts.hour}:${parts.minute}`;
};

export const normalizeScheduleDateTimePayload = (value) => String(value || '').trim();

export const buildScheduleDateTimePayload = (dateKey, timeValue) => {
  const date = normalizeScheduleDateTimePayload(dateKey);
  const time = normalizeScheduleDateTimePayload(timeValue);
  if (!date || !time) return '';
  return `${date}T${time}`;
};
const parseFiscalYearBounds = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number' && Number.isFinite(value)) {
    return { startYear: value - 1, endYear: value };
  }

  const normalized = String(value).trim();

  if (/^\d{4}$/.test(normalized)) {
    const endYear = Number(normalized);
    return { startYear: endYear - 1, endYear };
  }

  const rangeMatch = normalized.match(/^(\d{4})\s*-\s*(\d{4})$/);
  if (rangeMatch) {
    return {
      startYear: Number(rangeMatch[1]),
      endYear: Number(rangeMatch[2]),
    };
  }

  return null;
};

export const formatPublicFiscalYear = (value, { includeCode = true } = {}) => {
  const bounds = parseFiscalYearBounds(value);
  if (!bounds) return value ? String(value) : '';

  const label = `July 1, ${bounds.startYear} - June 30, ${bounds.endYear}`;
  if (!includeCode) return label;

  return `${label} (FY${String(bounds.endYear).slice(-2)})`;
};

export const getCurrentFiscalYear = (date = new Date()) => {
  return date.getMonth() < 6 ? date.getFullYear() : date.getFullYear() + 1;
};

export const isCurrentFiscalYear = (value, date = new Date()) => {
  if (value === null || value === undefined || value === '') return false;
  const bounds = parseFiscalYearBounds(value);
  const targetYear = bounds ? bounds.endYear : Number(value);
  return targetYear === getCurrentFiscalYear(date);
};

export const getFiscalYearBadge = (value, date = new Date()) => {
  if (value === null || value === undefined || value === '') return null;
  const isCurrent = isCurrentFiscalYear(value, date);
  const bounds = parseFiscalYearBounds(value);
  const yearCode = bounds ? `FY${String(bounds.endYear).slice(-2)}` : `FY${value}`;
  return {
    isCurrent,
    label: isCurrent ? `${yearCode} (Current)` : `${yearCode} (Historical)`,
    tone: isCurrent
      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
      : "bg-amber-100 text-amber-800 border-amber-200",
  };
};

export const formatMatchedAliasExplanation = (matchedAlias, canonicalAddress) => {
  const alias = String(matchedAlias || "").trim();
  const canonical = String(canonicalAddress || "").trim();
  if (alias && canonical && alias.toUpperCase() !== canonical.toUpperCase()) {
    return `${alias} (Matched alias for legal parcel: ${canonical})`;
  }
  return alias || canonical || "";
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const describeDueStatus = (value, { hideWhenMissing = false } = {}) => {
  const date = normalizeDateInput(value);
  if (!date) {
    if (hideWhenMissing) return null;
    return { label: 'Not scheduled', tone: 'text-gray-500', status: 'unscheduled' };
  }

  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / DAY_IN_MS);

  if (diffMs < 0) {
    const daysPast = Math.abs(diffDays);
    return {
      label: daysPast ? `Past due ${daysPast}d` : 'Past due',
      tone: 'text-rose-600',
      status: 'overdue',
    };
  }

  if (diffDays === 0) {
    return {
      label: 'Due today',
      tone: 'text-amber-600',
      status: 'today',
    };
  }

  if (diffDays <= 2) {
    return {
      label: `Due in ${diffDays}d`,
      tone: 'text-amber-600',
      status: 'soon',
    };
  }

  if (diffDays <= 7) {
    return {
      label: `Upcoming ${toEasternLocaleDateString(date)}`,
      tone: 'text-indigo-600',
      status: 'upcoming',
    };
  }

  return {
    label: `Due ${toEasternLocaleDateString(date)}`,
    tone: 'text-gray-500',
    status: 'scheduled',
  };
};

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'heic', 'heif', 'tif', 'tiff', 'svg']);

const extractFilename = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    try {
      const base = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://example.com';
      const url = new URL(value, base);
      const lastSegment = url.pathname.split('/').pop() || '';
      return decodeURIComponent(lastSegment);
    } catch (e) {
      const parts = value.split('/');
      return decodeURIComponent(parts.pop() || value);
    }
  }

  if (typeof value === 'object') {
    if (value.filename) return value.filename;
    if (value.name) return value.name;
    if (value.url) return extractFilename(value.url);
  }

  return '';
};

const getNormalizedFilename = (attachment) => {
  const raw = extractFilename(attachment);
  return raw ? raw.toLowerCase() : '';
};

export const getAttachmentFilename = (attachment, fallbackLabel = '') => {
  const filename = extractFilename(attachment);
  if (filename) return filename;
  return fallbackLabel;
};

export const getAttachmentExtension = (attachment) => {
  const normalized = getNormalizedFilename(attachment);
  if (!normalized.includes('.')) return '';
  return normalized.split('.').pop() || '';
};

export const isImageAttachment = (attachment) => {
  if (!attachment) return false;

  const contentType = (attachment.content_type || '').toLowerCase();
  if (contentType.startsWith('image/')) return true;

  const extension = getAttachmentExtension(attachment);
  if (!extension) return false;

  return IMAGE_EXTENSIONS.has(extension);
};

const CONTENT_TYPE_LABELS = {
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'text/plain': 'TXT',
  'application/rtf': 'RTF'
};

export const getAttachmentDisplayLabel = (attachment) => {
  const extension = getAttachmentExtension(attachment);
  if (extension) return extension.toUpperCase();

  const contentType = (attachment?.content_type || '').toLowerCase();
  if (contentType) {
    if (CONTENT_TYPE_LABELS[contentType]) {
      return CONTENT_TYPE_LABELS[contentType];
    }

    const subtype = contentType.split('/').pop() || '';
    if (subtype && subtype.length <= 6) {
      return subtype.toUpperCase();
    }
  }

  return 'FILE';
};

// Mirrors the backend calculate_fee() in routes/applications.py. Keep the two
// in sync: the first matching tiered/conditional/per_unit rule sets the base
// fee, and every additive rule accumulates on top of it.
export function calculateFeeFromRules(licenseType, extraData) {
  const rules = licenseType?.fee_rules || [];
  const data = extraData || {};

  const defaultFee = licenseType?.fee;
  let baseFee = defaultFee != null ? parseFloat(defaultFee) : null;
  let baseFeeSet = false;
  let additional = 0;

  for (const rule of rules) {
    const fieldValue = data[rule.field];

    if (rule.type === "tiered" && !baseFeeSet) {
      if (fieldValue == null || fieldValue === "") continue;
      const val = parseFloat(fieldValue);
      if (isNaN(val)) continue;
      for (const tier of rule.tiers || []) {
        const aboveMin = tier.min == null || val >= parseFloat(tier.min);
        const belowMax = tier.max == null || val <= parseFloat(tier.max);
        if (aboveMin && belowMax) {
          baseFee = parseFloat(tier.fee);
          baseFeeSet = true;
          break;
        }
      }
    } else if (rule.type === "conditional" && !baseFeeSet) {
      if (fieldValue == null || fieldValue === "") continue;
      const match = (rule.conditions || []).find(
        c => String(fieldValue).trim().toLowerCase() === String(c.value).trim().toLowerCase()
      );
      if (match) {
        baseFee = parseFloat(match.fee);
        baseFeeSet = true;
      } else if (rule.default != null) {
        baseFee = parseFloat(rule.default);
        baseFeeSet = true;
      }
    } else if (rule.type === "per_unit" && !baseFeeSet) {
      if (fieldValue == null || fieldValue === "") continue;
      const val = parseFloat(fieldValue);
      const rate = parseFloat(rule.rate ?? 0);
      if (!isNaN(val) && val > 0) {
        baseFee = val * rate;
        baseFeeSet = true;
      }
    } else if (rule.type === "additive") {
      if (fieldValue == null) continue;
      const selected = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
      for (const option of rule.options || []) {
        if (!selected.includes(option.value)) continue;
        const feePer = parseFloat(option.fee ?? 0);
        const qtyField = option.quantity_field;
        if (qtyField) {
          const parsedQty = parseFloat(data[qtyField]);
          const qty = isNaN(parsedQty) ? 1 : Math.max(1, parsedQty);
          additional += feePer * qty;
        } else {
          additional += feePer;
        }
      }
    }
  }

  if (baseFee == null && additional === 0) return defaultFee ?? null;
  return (baseFee ?? 0) + additional;
}

export function getFeeCalculationBreakdown(licenseType, extraData = {}) {
  const rawName = String(licenseType?.name || "").toLowerCase().replace(/[-_]/g, " ").trim();
  const data = extraData || {};

  if (rawName.includes("multi family") || rawName.includes("multifamily")) {
    const units = Number(data.num_units);
    if (Number.isFinite(units) && units > 0) {
      const total = units * 125;
      return {
        fee: total,
        calculationText: `${units} unit${units === 1 ? "" : "s"} × $125.00/unit = $${total.toFixed(2)}`,
        formula: `${units} × $125.00`,
      };
    }
    const fallback = Number(licenseType?.fee || 0);
    return {
      fee: fallback,
      calculationText: `Base fee: $${fallback.toFixed(2)}`,
      formula: `$${fallback.toFixed(2)}`,
    };
  }

  if (rawName.includes("single family")) {
    const fee = Number(licenseType?.fee ?? 200);
    return {
      fee,
      calculationText: `Standard residential rental fee: $${fee.toFixed(2)}`,
      formula: `$${fee.toFixed(2)}`,
    };
  }

  if (rawName.includes("business")) {
    const tierFees = {
      "Non-Food $150": 150,
      "Food + Non-Alcohol $150": 150,
      "Off-site beer & wine $250": 250,
      "Off-site beer/wine/liquor $300": 300,
      "On-site beer & wine $250": 250,
      "On-site beer/wine/liquor $300": 300,
      "On-site & off-site beer & wine $280": 280,
      "On-site & off-site beer/wine/liquor $300": 300,
    };
    const tierName = data.license_fee_tier;
    const baseFee = tierFees[tierName] ?? Number(licenseType?.fee ?? 150);
    const parts = [tierName ? `${tierName} ($${baseFee.toFixed(2)})` : `Base: $${baseFee.toFixed(2)}`];
    let total = baseFee;

    const fees = Array.isArray(data.additional_fees)
      ? data.additional_fees
      : data.additional_fees ? [data.additional_fees] : [];
    if (fees.includes("Alarm Fee $40")) {
      total += 40;
      parts.push("Alarm Fee ($40.00)");
    }
    if (fees.includes("Coin-Op Vending Machine $20/machine")) {
      const machines = Math.max(1, Number(data.num_vending_machines) || 1);
      const vendingTotal = machines * 20;
      total += vendingTotal;
      parts.push(`${machines} Vending Machine${machines === 1 ? "" : "s"} × $20.00 ($${vendingTotal.toFixed(2)})`);
    }

    return {
      fee: total,
      calculationText: parts.length > 1 ? `${parts.join(" + ")} = $${total.toFixed(2)}` : parts[0],
      formula: parts.join(" + "),
    };
  }

  // Generic fee rules
  const rules = licenseType?.fee_rules || [];
  const defaultFee = licenseType?.fee;
  let baseFee = defaultFee != null ? parseFloat(defaultFee) : null;
  let baseFeeSet = false;
  let additional = 0;
  const parts = [];

  for (const rule of rules) {
    const fieldValue = data[rule.field];
    if (rule.type === "per_unit" && !baseFeeSet) {
      if (fieldValue != null && fieldValue !== "") {
        const val = parseFloat(fieldValue);
        const rate = parseFloat(rule.rate ?? 0);
        if (!isNaN(val) && val > 0) {
          baseFee = val * rate;
          baseFeeSet = true;
          parts.push(`${val} unit${val === 1 ? "" : "s"} × $${rate.toFixed(2)}/unit`);
        }
      }
    } else if (rule.type === "tiered" && !baseFeeSet) {
      if (fieldValue != null && fieldValue !== "") {
        const val = parseFloat(fieldValue);
        if (!isNaN(val)) {
          for (const tier of rule.tiers || []) {
            const aboveMin = tier.min == null || val >= parseFloat(tier.min);
            const belowMax = tier.max == null || val <= parseFloat(tier.max);
            if (aboveMin && belowMax) {
              baseFee = parseFloat(tier.fee);
              baseFeeSet = true;
              parts.push(`Tier (${val} units): $${baseFee.toFixed(2)}`);
              break;
            }
          }
        }
      }
    } else if (rule.type === "conditional" && !baseFeeSet) {
      if (fieldValue != null && fieldValue !== "") {
        const match = (rule.conditions || []).find(
          c => String(fieldValue).trim().toLowerCase() === String(c.value).trim().toLowerCase()
        );
        if (match) {
          baseFee = parseFloat(match.fee);
          baseFeeSet = true;
          parts.push(`${fieldValue}: $${baseFee.toFixed(2)}`);
        }
      }
    } else if (rule.type === "additive") {
      if (fieldValue != null) {
        const selected = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
        for (const option of rule.options || []) {
          if (!selected.includes(option.value)) continue;
          const feePer = parseFloat(option.fee ?? 0);
          const qtyField = option.quantity_field;
          if (qtyField) {
            const parsedQty = parseFloat(data[qtyField]);
            const qty = isNaN(parsedQty) ? 1 : Math.max(1, parsedQty);
            additional += feePer * qty;
            parts.push(`${option.value} (${qty} × $${feePer.toFixed(2)})`);
          } else {
            additional += feePer;
            parts.push(`${option.value} ($${feePer.toFixed(2)})`);
          }
        }
      }
    }
  }

  const finalFee = (baseFee ?? defaultFee ?? 0) + additional;
  if (parts.length > 0) {
    return {
      fee: finalFee,
      calculationText: `${parts.join(" + ")} = $${finalFee.toFixed(2)}`,
      formula: parts.join(" + "),
    };
  }
  return {
    fee: finalFee,
    calculationText: finalFee != null ? `Flat fee: $${Number(finalFee).toFixed(2)}` : "—",
    formula: finalFee != null ? `$${Number(finalFee).toFixed(2)}` : "",
  };
}

export const appendGeoMetadata = async (formData, { fieldName = 'capture_metadata', timeoutMs = 6000, extraMetadata = {} } = {}) => {
  if (!formData || typeof formData.append !== 'function') return null;
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;

  const startedAt = Date.now();
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        try {
          const { coords, timestamp } = position || {};
          const payload = {
            lat: coords?.latitude,
            lng: coords?.longitude,
            accuracy: coords?.accuracy,
            altitude: coords?.altitude,
            altitude_accuracy: coords?.altitudeAccuracy,
            heading: coords?.heading,
            speed: coords?.speed,
            captured_at: timestamp ? new Date(timestamp).toISOString() : new Date().toISOString(),
            requested_at: new Date(startedAt).toISOString(),
            source: 'browser_geolocation',
          };
          const filtered = Object.fromEntries(
            Object.entries({ ...payload, ...extraMetadata }).filter(([, v]) => v !== null && v !== undefined),
          );
          formData.append(fieldName, JSON.stringify(filtered));
          resolve(filtered);
        } catch (err) {
          resolve(null);
        }
      },
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
};

// Applicants reminded within this many days are excluded from a bulk payment
// reminder by default, to avoid over-emailing. Keep in sync with
// REMINDER_COOLDOWN_DAYS in FastAPI/CiviCodeAPI/routes/applications.py.
export const REMINDER_COOLDOWN_DAYS = 7;

const getReminderPaymentHistory = (app) =>
  Array.isArray(app?.extra_data?.payment_history) ? app.extra_data.payment_history : [];

// When was this applicant last sent a payment reminder? Prefer the canonical
// payment_sent_at timestamp; fall back to the most recent payment_request_email
// event recorded in payment_history.
export const getLastReminderAt = (app) => {
  if (app?.payment_sent_at) return app.payment_sent_at;
  const history = getReminderPaymentHistory(app);
  for (let i = history.length - 1; i >= 0; i -= 1) {
    if (history[i]?.event === "payment_request_email" && history[i]?.timestamp) {
      return history[i].timestamp;
    }
  }
  return null;
};

export const getLatestPaymentRequestEmailEvent = (app) => {
  const history = getReminderPaymentHistory(app);
  for (let i = history.length - 1; i >= 0; i -= 1) {
    if (history[i]?.event === "payment_request_email") {
      return history[i];
    }
  }
  return null;
};

export const getPaymentRequestDeliveryFeedback = (app, { isAdmin = false } = {}) => {
  const emailEvent = getLatestPaymentRequestEmailEvent(app);
  const appPrefix = app?.id ? `Application #${app.id}: ` : "";
  const quickbooks = app?.extra_data?.quickbooks || null;
  const invoiceNumber = quickbooks?.invoice_doc_number || quickbooks?.invoice_id;

  if (emailEvent?.result === "sent") {
    const invoiceText = isAdmin && invoiceNumber ? ` for invoice #${invoiceNumber}` : "";
    return {
      tone: "success",
      text: `${appPrefix}payment request email accepted for delivery${invoiceText}.`,
    };
  }

  if (emailEvent?.result === "no_recipient") {
    return {
      tone: "error",
      text: `${appPrefix}payment request was posted in the portal, but no applicant email address was available.`,
    };
  }

  if (emailEvent?.result === "failed") {
    return {
      tone: "error",
      text: `${appPrefix}payment request was posted in the portal, but the email was not sent. Check the Email Dashboard before relying on it.`,
    };
  }

  return {
    tone: "error",
    text: `${appPrefix}payment request was posted, but email delivery status was not returned. Check the Email Dashboard before relying on it.`,
  };
};

const isApplicationPaid = (app) =>
  ["paid", "license_pending_approval", "license_issued"].includes(app?.status) || Boolean(app?.paid_at);

// Split a set of selected applications into the categories the bulk reminder
// review step renders. Mirrors the server-side guard so the preview matches
// what actually sends.
//   - eligible: will be emailed
//   - recentlyReminded: within the cooldown window (excluded unless opted in)
//   - paid: already paid (never sent)
//   - noEmail: no recipient email on file (never sent)
export const bucketRecipientsForReminder = (
  apps = [],
  { cooldownDays = REMINDER_COOLDOWN_DAYS, includeRecentlyReminded = false } = {},
) => {
  const now = Date.now();
  const cutoff = now - cooldownDays * 24 * 60 * 60 * 1000;
  const buckets = { eligible: [], recentlyReminded: [], paid: [], noEmail: [] };

  for (const app of apps) {
    if (isApplicationPaid(app)) {
      buckets.paid.push(app);
      continue;
    }
    if (!app?.email) {
      buckets.noEmail.push(app);
      continue;
    }
    const lastReminderAt = getLastReminderAt(app);
    const lastReminderMs = lastReminderAt ? new Date(lastReminderAt).getTime() : null;
    const isRecent = lastReminderMs != null && !Number.isNaN(lastReminderMs) && lastReminderMs >= cutoff;
    if (isRecent && !includeRecentlyReminded) {
      buckets.recentlyReminded.push(app);
    } else {
      buckets.eligible.push(app);
    }
  }

  return buckets;
};
